import { User } from "../../../../../pages/shared/models/user/user";
import { ActionBar } from "../../../../../pages/components/action-bar/action-bar";
import BarekamText from "../../../../../pages/components/text";
import { Message, MessageSections } from "../../../../../pages/components/message/message";
import { BalanceCardContent, BalanceContainer, BalancePageCard } from "../../../../../pages/user/balance/styled";
import React, { useEffect, useState } from "react";
import { BalanceModel } from "../../../../../redux/reducers/balance-reducers";
import { getBindings } from "../../../../../redux/slices/balance-slice";
import { getCurrencies } from "../../../../../redux/slices/static-data.slice";
import { useAppDispatch } from "../../../../../redux/store";
import balanceService from "../../../../../services/balance-service";
import { StandardDialog } from "../../../../../pages/components/dialogs/standard-dialog/standard-dialog";
import AdminTopUpDialog from "../components/admin-top-up-dialog";
import AdminGrantDiscountDialog from "../components/admin-grand-discount-dialog";
import allUsersService from "../../../../../services/all-users.service";
import { BalanceActions } from "../../../../actions/actions-by-state";
import { ConfirmationDialog } from "../../../../../pages/components/dialogs/confirmation-dialog";
import { BalanceActionTypes } from "../../../../actions/action-types.enum";
import { ActionDialog } from "../../../../actions/dialogs/action-dialog";

const CustomerBalance = ({ customer }: { customer: User | undefined }) => {
    const [isReady, setIsReady] = useState<boolean>(false);

    const [balance, setBalance] = useState([] as BalanceModel[]);
    const [discount, setDiscount] = useState<number>();
    const [bonus, setBonus] = useState([] as BalanceModel[]);
    const [topUpOpen, setTopUpOpen] = useState<boolean>(false);
    const [grandDiscountOpen, setGrantDiscountOpen] = useState<boolean>(false);
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const [openRefillConfirmationPopup, setOpenRefillConfirmationPopup] = useState<boolean>(false);
    const [activeAction, setActiveAction] = useState<string>();

    const amdBalanceModel = balance.find(balanceModel => balanceModel.currency.currencyId === 2);
    const bonusBalanceModel = bonus.find(balanceModel => balanceModel.currency.currencyId === 2);
    const amdAmount = amdBalanceModel ? amdBalanceModel.amount : 0;
    const amdBonus = bonusBalanceModel ? bonusBalanceModel.amount : 0;

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (customer && customer.userId) {
            Promise.allSettled([
                balanceService.getBalance(customer.userId),
                dispatch(getCurrencies()),
                dispatch(getBindings())
            ]).then(([balanceResponse]) => {
                if (balanceResponse.status === 'fulfilled') {
                    setBalance(balanceResponse.value?.data || [])
                }
                setDiscount(customer.discountPercent);
                setIsReady(true)
            })
        }
    }, []);


    function topUpBalance() {
        setTopUpOpen(true);
    }

    function grantDiscount() {
        setGrantDiscountOpen(true);
    }

    function handleRefillDialogClosed(withUpdate: boolean) {
        if (withUpdate && customer?.userId) {
            setOpenRefillConfirmationPopup(true)
        } else {
            setTopUpOpen(false);
        }
    }

    function handleRefillConfirmationDialogClose(withUpdate: boolean) {
        if (withUpdate && customer?.userId) {
            balanceService.getBalance(customer.userId)
                .then(balanceResponse => {
                    if (balanceResponse) {
                        setBalance(balanceResponse.data || [])
                    }
                })

        }
        setTopUpOpen(false);
        setOpenRefillConfirmationPopup(false);
    }

    function handleGrantDiscountDialogClosed(withUpdate: boolean) {
        if (withUpdate && customer?.userId) {
            setOpenConfirmationPopup(true);
        } else {
            setGrantDiscountOpen(false);
        }
    }

    function handleConfirmationDialogClose(withUpdate: boolean) {
        if (withUpdate && customer?.userId) {
            allUsersService.getUserById(customer.userId)
                .then(userResponse => {
                    if (userResponse) {
                        setDiscount(userResponse.data?.discountPercent || 0);
                    }
                })
        }
        setGrantDiscountOpen(false);
        setOpenConfirmationPopup(false);
    }

    function onActionClick(actionId: string) {
        if (actionId === BalanceActionTypes.TOP_UP) {
            topUpBalance();
        } else if (actionId === BalanceActionTypes.GRANT_DISCOUNT) {
            grantDiscount();
        } else if (actionId === BalanceActionTypes.SEE_BALANCE_HISTORY || actionId === BalanceActionTypes.SEE_BONUS_HISTORY) {
            setActiveAction(actionId);
        }
    }

    function getHistoryDialogProps() {
        if (activeAction === BalanceActionTypes.SEE_BALANCE_HISTORY) {
            return balance[1]?.balanceId ? { category: 'Balance', categoryId: balance[1]?.balanceId } : undefined
        } else if (activeAction === BalanceActionTypes.SEE_BONUS_HISTORY) {
            return bonus[1]?.balanceId ? { category: 'Balance', categoryId: bonus[1]?.balanceId } : undefined
        }
        return undefined;
    }

    if (isReady && customer && customer.userId) {
        return (
            <>
                <BalanceContainer>
                    <ActionBar actions={BalanceActions}
                        actionFunction={onActionClick} />

                    <BalancePageCard>
                        <BalanceCardContent>
                            <BarekamText variant={"m"} opaque>
                                <Message messageKey={'current_balance'} sectionId={MessageSections.MAIN} />
                            </BarekamText>
                            <BarekamText variant={"l"} bold>
                                {amdAmount} {' '}
                                <Message messageKey={'amd'} sectionId={MessageSections.MAIN} />
                            </BarekamText>
                        </BalanceCardContent>
                        <BalanceCardContent>
                            <BarekamText variant={"m"} opaque>
                                <Message messageKey={'bonuses'} sectionId={MessageSections.MAIN} />
                            </BarekamText>
                            <BarekamText variant={"l"} bold>
                                {amdBonus} {' '}
                                <Message messageKey={'amd'}
                                    sectionId={MessageSections.MAIN} />
                            </BarekamText>
                        </BalanceCardContent>
                        <BalanceCardContent>
                            <BarekamText variant={"m"} opaque>
                                <Message messageKey={'discount'} sectionId={MessageSections.MAIN} />
                            </BarekamText>
                            <BarekamText variant={"l"} bold>
                                {discount} {' %'}
                            </BarekamText>
                        </BalanceCardContent>
                    </BalancePageCard>
                </BalanceContainer>

                {!openRefillConfirmationPopup && (
                    <StandardDialog
                        headerLabel={<Message messageKey={'top_up_balance'} sectionId={MessageSections.MAIN} />}
                        open={topUpOpen}>
                        <AdminTopUpDialog userId={customer.userId}
                            onClose={handleRefillDialogClosed} />
                    </StandardDialog>
                )}

                {openRefillConfirmationPopup && (
                    <ConfirmationDialog open={openRefillConfirmationPopup}
                        handleClose={handleRefillConfirmationDialogClose}
                        titleKey={'confirm_refill_balance'}
                        descriptionKey={'confirm_refill_balance_description'} />
                )}

                <StandardDialog
                    headerLabel={<Message messageKey={'grand_discount'} sectionId={MessageSections.MAIN} />}
                    open={grandDiscountOpen}>
                    {!openConfirmationPopup && (
                        <AdminGrantDiscountDialog userId={customer.userId}
                            onClose={handleGrantDiscountDialogClosed} />
                    )}

                    {openConfirmationPopup && (
                        <ConfirmationDialog open={openConfirmationPopup}
                            handleClose={handleConfirmationDialogClose}
                            titleKey={'confirm_grant_discount'}
                            descriptionKey={'confirm_grant_discount_description'} />
                    )}
                </StandardDialog>

                <ActionDialog open={!!activeAction}
                    closePopup={() => setActiveAction(undefined)}
                    action={activeAction}
                    historyDialogProps={getHistoryDialogProps()} />
            </>
        )
    } else {
        return <></>
    }
};

export default CustomerBalance;
